// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventContextAction } from '@/audit/api/types';
import {
  ArrowForwardRounded,
  LaunchRounded,
} from '@/constants/hardfin-standard-icons';
import { createViewableURLString } from '@/utilities/create-viewable-url-string';
import { Link, Stack, Tooltip, Typography } from '@mui/material';

interface URLLinkValue {
  id: string;
  key: string;
  name: string | null;
  url: string;
}

interface Props {
  property: {
    id: string;
    name: string;
    type: 'URL_LINK';
    newValue: URLLinkValue | null;
    oldValue?: URLLinkValue | null;
  };
  action: AuditEventContextAction;
}

interface URLLinkDisplayProps {
  urlLink: URLLinkValue;
}

/**
 * Renders a URL link value with name and clickable URL.
 * Shows the name if available, otherwise shows the URL as the display text.
 * Always shows the URL below the name if both are present.
 */
function URLLinkDisplay(props: URLLinkDisplayProps) {
  const viewableURL = createViewableURLString(props.urlLink.url);

  if (viewableURL === null) {
    return (
      <Tooltip title="URL is invalid or may be unsafe to link directly" arrow>
        <Stack
          spacing={0.5}
          direction="row"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <Typography variant="caption" component="span" color="error.main">
            {props.urlLink.url}
          </Typography>
          {props.urlLink.name && (
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
            >
              ({props.urlLink.name})
            </Typography>
          )}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Link
      href={viewableURL}
      target="_blank"
      rel="noopener"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <Stack spacing={0.5} direction="row">
        <Typography variant="caption" component="span">
          {props.urlLink.url}
        </Typography>
        {props.urlLink.name && (
          <Typography variant="caption" component="span" color="text.secondary">
            ({props.urlLink.name})
          </Typography>
        )}
      </Stack>
      <LaunchRounded sx={{ fontSize: '0.75rem' }} color="action" />
    </Link>
  );
}

/**
 * Component that displays URL link changes in audit events.
 * Shows the link name and URL with proper linking functionality.
 * Handles CREATE, UPDATE, and DELETE actions appropriately.
 */
export function AuditUrlLinkLabel(props: Props) {
  return (
    <Typography display="flex" variant="caption" alignItems="center">
      <Typography component="span" variant="inherit" fontWeight="bold">
        {props.property.name}:&nbsp;
      </Typography>

      {/* Show old value for UPDATE and DELETE */}
      {props.property.oldValue && (
        <URLLinkDisplay urlLink={props.property.oldValue} />
      )}

      {/* Show the arrow only for a true old->new change, not an add or remove */}
      {props.property.oldValue && props.property.newValue && (
        <>
          {' '}
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
        </>
      )}

      {/* Show new value for CREATE and UPDATE */}
      {props.property.newValue &&
        (props.action === 'CREATE' || props.action === 'UPDATE') && (
          <URLLinkDisplay urlLink={props.property.newValue} />
        )}
    </Typography>
  );
}
